// Find My Childcare — async data hooks and loading/error primitives.
import * as React from 'react';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export interface UseApiResult<T> extends AsyncState<T> {
  reload: () => void;
}

// useApi: run an async function whenever deps change. Cancels stale results.
//   const { data, loading, error, reload } = useApi(() => api.providers.search(q), [q.zip]);
export function useApi<T>(fn: () => Promise<T>, deps: React.DependencyList = []): UseApiResult<T> {
  const [state, setState] = React.useState<AsyncState<T>>({
    data: null,
    loading: true,
    error: null,
  });
  const [nonce, setNonce] = React.useState(0);

  // We intentionally re-run when any item in `deps` changes; consumers own that contract.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are explicitly passed
  React.useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve()
      .then(fn)
      .then((data) => {
        if (alive) setState({ data, loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (alive) {
          setState({
            data: null,
            loading: false,
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      });
    return () => {
      alive = false;
    };
  }, [...deps, nonce]);

  return { ...state, reload: () => setNonce((n) => n + 1) };
}

export interface UseAsyncActionResult<TArgs extends unknown[], TResult> {
  loading: boolean;
  error: Error | null;
  run: (...args: TArgs) => Promise<TResult>;
}

// useAsyncAction: wrap a mutating call so screens get loading + error for writes.
//   const update = useAsyncAction(api.providers.updateAvailability);
//   await update.run(id, ages);
export function useAsyncAction<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
): UseAsyncActionResult<TArgs, TResult> {
  const [state, setState] = React.useState<{ loading: boolean; error: Error | null }>({
    loading: false,
    error: null,
  });
  const run = React.useCallback(
    async (...args: TArgs) => {
      setState({ loading: true, error: null });
      try {
        const result = await fn(...args);
        setState({ loading: false, error: null });
        return result;
      } catch (error: unknown) {
        const wrapped = error instanceof Error ? error : new Error(String(error));
        setState({ loading: false, error: wrapped });
        throw wrapped;
      }
    },
    [fn],
  );
  return { ...state, run };
}

// --- Loading + error primitives ---------------------------------------

interface SkeletonProps {
  height?: number | string;
  width?: number | string;
  radius?: number;
  style?: React.CSSProperties;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  height = 16,
  width = '100%',
  radius = 8,
  style = {},
}) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block',
      width,
      height,
      borderRadius: radius,
      background: 'linear-gradient(90deg, var(--bg-2) 0%, var(--line) 50%, var(--bg-2) 100%)',
      backgroundSize: '200% 100%',
      animation: 'fmc-shimmer 1.4s ease-in-out infinite',
      ...style,
    }}
  />
);

export const CardSkeleton: React.FC = () => (
  <div
    style={{
      background: 'var(--surface)',
      border: '1px solid var(--line)',
      borderRadius: 'var(--r-md)',
      overflow: 'hidden',
    }}
  >
    <div style={{ padding: 10 }}>
      <Skeleton height={180} radius={10} />
    </div>
    <div style={{ padding: '6px 18px 18px', display: 'grid', gap: 8 }}>
      <Skeleton height={18} width="70%" />
      <Skeleton height={13} width="50%" />
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <Skeleton height={20} width={60} radius={999} />
        <Skeleton height={20} width={70} radius={999} />
      </div>
      <Skeleton height={40} radius={12} style={{ marginTop: 10 }} />
    </div>
  </div>
);

interface ErrorStateProps {
  error: Error | null | undefined;
  onRetry?: () => void;
  compact?: boolean;
}

export const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry, compact = false }) => (
  <div
    role="alert"
    style={{
      padding: compact ? '14px 18px' : '40px 24px',
      background: 'var(--coral-soft, #FCE8E4)',
      border: '1px solid rgba(238,85,71,0.30)',
      borderRadius: 'var(--r-md)',
      textAlign: compact ? 'left' : 'center',
      color: 'var(--ink-2)',
      display: compact ? 'flex' : 'block',
      alignItems: 'center',
      gap: 12,
    }}
  >
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>
        Something went wrong.
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
        {error?.message ?? 'Unable to load this data right now.'}
      </div>
    </div>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        style={{
          background: 'var(--ink)',
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          padding: '8px 14px',
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 13,
        }}
      >
        Try again
      </button>
    )}
  </div>
);
